import ApiClient from './api-client';
import type { EntityFacets } from '@shared/types/status-filter.types';

export interface StandardEntityParams {
  page?: number;
  pageSize?: number;
  query?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface EntityListResponse<T> {
  data: T[];
  results?: T[];
  total: number;
  totalElements?: number;
  facets?: EntityFacets;
  page?: number;
  pageNumber?: number;
  pageSize?: number;
  totalPages?: number;
}

export interface NavigationFilters {
  pageSize?: number;
  statusFilter?: string;
  query?: string;
  [key: string]: unknown;
}

export interface ServiceConfig {
  baseURL?: string;
  apiKey?: string;
  entityName: string;
}

export abstract class BaseService<TEntity, TListResponse, _TParams> {
  protected apiClient: ApiClient;
  protected readonly defaultHeaders: Record<string, string>;
  protected readonly entityName: string;
  private abortController: AbortController | null = null;

  constructor(config: ServiceConfig) {
    this.apiClient = new ApiClient(config.baseURL || '');
    this.defaultHeaders = {
      'X-API-Key': config.apiKey || '',
    };
    this.entityName = config.entityName;
  }

  /**
   * Cancel any in-flight requests for this service
   */
  cancel(): void {
    this.abortController?.abort();
    this.abortController = null;
  }

  /**
   * Create a fresh AbortSignal for the next request
   */
  protected getSignal(): AbortSignal {
    this.abortController?.abort();
    this.abortController = new AbortController();
    return this.abortController.signal;
  }

  abstract getEntitiesForNavigation(
    page: number,
    filters: NavigationFilters
  ): Promise<TListResponse>;

  abstract getEntityById(id: string): Promise<TEntity>;

  protected createServiceError(operation: string, error: unknown): Error {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return new Error(`Failed to ${operation} ${this.entityName}: ${message}`);
  }

}